"use client"

import { Outlet, useParams } from "react-router"
import { createContext, useContext, useState } from "react"
import { apiRemote } from "@/api/wp-api"
import { useAlert } from "@/contexts/AlertContext"
import type { IMyLeanbotExtend } from "./My-Leanbot"

export interface MyLeanbotContextType {
    getDataMyLeanbot: () => Promise<any>
    validateLeanbotId: (id: string) => Promise<{ code?: number; valid: boolean; message?: string }>
    registerLeanbot: (id: string) => Promise<{ success: boolean; message?: string }>
    uploadAvatarLeanbot: (
        leanbotId: string,
        file: File,
    ) => Promise<{ success: boolean; message?: string; image_url?: string }>
}

const MyLeanbotContext = createContext<MyLeanbotContextType>({} as any)

export function MyLeanbotsLayout() {
    const { id } = useParams<{ id: string }>()
    const alert = useAlert()
    const [myLeanbotsData, setMyLeanbotsData] = useState<IMyLeanbotExtend>({} as any)

    const getDataMyLeanbot = () => {
        return apiRemote.getDataMyLeanbots().catch((err: any) => {
            alert.error(err.message)
            return {} as IMyLeanbotExtend // Trả về giá trị mặc định khi có lỗi
        })
    }

    const validateLeanbotId = async (id: string) => {
        try {
            // Call API to validate the Leanbot ID
            const response = await apiRemote.validateLeanbotId({ leanbot_id: id })
            // const response = await apiRemote.validateLeanbotId(id)
            return {
                code: response.code,
                valid: response.valid,
                message: response.message,
            }
        } catch (error: any) {
            alert.error(error.message || "Failed to validate Leanbot ID")
            return {
                valid: false,
                message: error.message || "Failed to validate Leanbot ID",
            }
        }
    }

    const registerLeanbot = async (id: string) => {
        try {
            // Call API to register the Leanbot
            // const response = await apiRemote.registerLeanbot(id)
            const response = await apiRemote.registerLeanbot({ leanbot_id: id })
            return {
                success: response.success,
                message: response.message,
            }
        } catch (error: any) {
            alert.error(error.message || "Failed to register Leanbot")
            return {
                success: false,
                message: error.message || "Failed to register Leanbot",
            }
        }
    }

    const uploadAvatarLeanbot = async (leanbotId: string, file: File) => {
        try {
            const formData = new FormData()
            formData.append("leanbot_id", leanbotId)
            formData.append("image", file)

            // Call API to upload the avatar
            const response = await apiRemote.uploadLeanbotAvatar(formData)

            return {
                success: response.success || response.code === 200,
                message: response.message,
                image_url: response.image_url || response.data?.image_url,
            }
        } catch (error: any) {
            alert.error(error.message || "Failed to upload Leanbot avatar")
            return {
                success: false,
                message: error.message || "Failed to upload Leanbot avatar",
            }
        }
    }

    return (
        <MyLeanbotContext.Provider value={{ getDataMyLeanbot, validateLeanbotId, registerLeanbot, uploadAvatarLeanbot }}>
            <Outlet />
        </MyLeanbotContext.Provider>
    )
}

export function useMyLeanbotContext() {
    return useContext(MyLeanbotContext)
}
